import { ProjectDocumentationForm, FileItem } from '../types/projectDocumentation';

export interface AdditionalData {
  selectedType: string;
  selectedSubCategory: string;
  selectedMaterialities: string[];
  selectedUses: string[];
}

const normalizeValue = (value: any): any => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return '';
    return trimmed;
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value;
  return value;
};

const compareValues = (original: any, current: any): boolean => {
  const normalizedOriginal = normalizeValue(original);
  const normalizedCurrent = normalizeValue(current);
  
  if (typeof normalizedOriginal === 'number' && typeof normalizedCurrent === 'number') {
    return normalizedOriginal === normalizedCurrent;
  }
  
  return normalizedOriginal === normalizedCurrent;
};

export interface ProjectDocumentationPayload {
  name: string;
  description: string;
  total_surface: number;
  covered_surface: number;
  semicovered_surface: number;
  uncovered_surface: number;
  environments: number;
  bedrooms: number;
  bathrooms: number;
  floors: number;
  garage: boolean;
  yard: boolean;
  terrace: boolean;
  pool: boolean;
  implantation: number;
  construction_type: string;
  stage: string;
  state?: string;
  budget: number;
  budget_currency: string;
  consultation_price: number;
  consultation_price_currency: string;
  price: number;
  price_currency: string;
  preliminary_project_subcategory_id: number;
  materiality_id: number;
  use_id: number;
  pause: boolean;
  user_id?: number;
}

export const buildProjectDocumentationPayload = (
  formData: ProjectDocumentationForm | any,
  additionalData: AdditionalData,
  isUpdate: boolean = false,
  userId?: number | string
): ProjectDocumentationPayload => {
  const payload: any = {
    name: formData.name,
    description: formData.description,
    total_surface: Number(formData.total_surface),
    covered_surface: Number(formData.covered_surface),
    semicovered_surface: Number(formData.semicovered_surface),
    uncovered_surface: Number(formData.uncovered_surface),
    environments: Number(formData.environments),
    bedrooms: Number(formData.bedrooms),
    bathrooms: Number(formData.bathrooms),
    floors: Number(formData.floors),
    garage: formData.garage,
    yard: formData.yard,
    terrace: formData.terrace,
    pool: formData.pool,
    implantation: Number(formData.implantation),
    construction_type: formData.construction_type,
    stage: formData.stage || additionalData.selectedType || 'anteproyecto',
    budget: Number(formData.budget),
    budget_currency: (formData as any).budget_currency || 'ARS',
    consultation_price: Number(formData.consultation_price),
    consultation_price_currency: (formData as any).consultation_price_currency || 'ARS',
    price: Number((formData as any).price || 0),
    price_currency: (formData as any).price_currency || 'ARS',
    preliminary_project_subcategory_id: Number(additionalData.selectedSubCategory),
    materiality_id: Number(additionalData.selectedMaterialities[0]),
    use_id: Number(additionalData.selectedUses[0]),
    pause: Boolean((formData as any).pause || false)
  };

  if (!isUpdate) {
    payload.state = 'pendiente';
    if (userId !== undefined && userId !== null) {
      payload.user_id = Number(userId);
    }
  }

  return payload;
};

export const buildPartialPayload = (
  originalFormData: ProjectDocumentationForm,
  originalAdditionalData: AdditionalData,
  newFormData: ProjectDocumentationForm,
  newAdditionalData: AdditionalData
): Partial<ProjectDocumentationPayload> => {
  const partial: any = {};

  if (!compareValues(originalFormData.name, newFormData.name)) {
    partial.name = newFormData.name;
  }

  if (!compareValues(originalFormData.description, newFormData.description)) {
    partial.description = newFormData.description;
  }

  if (!compareValues(originalFormData.total_surface, newFormData.total_surface)) {
    partial.total_surface = Number(newFormData.total_surface);
  }

  if (!compareValues(originalFormData.covered_surface, newFormData.covered_surface)) {
    partial.covered_surface = Number(newFormData.covered_surface);
  }

  if (!compareValues(originalFormData.semicovered_surface, newFormData.semicovered_surface)) {
    partial.semicovered_surface = Number(newFormData.semicovered_surface);
  }

  if (!compareValues(originalFormData.uncovered_surface, newFormData.uncovered_surface)) {
    partial.uncovered_surface = Number(newFormData.uncovered_surface);
  }

  if (!compareValues(originalFormData.environments, newFormData.environments)) {
    partial.environments = Number(newFormData.environments);
  }

  if (!compareValues(originalFormData.bedrooms, newFormData.bedrooms)) {
    partial.bedrooms = Number(newFormData.bedrooms);
  }

  if (!compareValues(originalFormData.bathrooms, newFormData.bathrooms)) {
    partial.bathrooms = Number(newFormData.bathrooms);
  }

  if (!compareValues(originalFormData.floors, newFormData.floors)) {
    partial.floors = Number(newFormData.floors);
  }

  if (!compareValues(originalFormData.garage, newFormData.garage)) {
    partial.garage = newFormData.garage;
  }

  if (!compareValues(originalFormData.yard, newFormData.yard)) {
    partial.yard = newFormData.yard;
  }

  if (!compareValues(originalFormData.terrace, newFormData.terrace)) {
    partial.terrace = newFormData.terrace;
  }

  if (!compareValues(originalFormData.pool, newFormData.pool)) {
    partial.pool = newFormData.pool;
  }

  if (!compareValues(originalFormData.implantation, newFormData.implantation)) {
    partial.implantation = Number(newFormData.implantation);
  }

  if (!compareValues(originalFormData.construction_type, newFormData.construction_type)) {
    partial.construction_type = newFormData.construction_type;
  }

  const originalStage = originalFormData.stage || originalAdditionalData.selectedType || 'anteproyecto';
  const newStage = newFormData.stage || newAdditionalData.selectedType || 'anteproyecto';
  if (!compareValues(originalStage, newStage)) {
    partial.stage = newStage;
  }

  if (!compareValues(originalFormData.budget, newFormData.budget)) {
    partial.budget = Number(newFormData.budget);
  }

  const originalBudgetCurrency = (originalFormData as any).budget_currency || 'ARS';
  const newBudgetCurrency = (newFormData as any).budget_currency || 'ARS';
  if (!compareValues(originalBudgetCurrency, newBudgetCurrency)) {
    partial.budget_currency = newBudgetCurrency;
  }

  if (!compareValues(originalFormData.consultation_price, newFormData.consultation_price)) {
    partial.consultation_price = Number(newFormData.consultation_price);
  }

  const originalConsultationCurrency = (originalFormData as any).consultation_price_currency || 'ARS';
  const newConsultationCurrency = (newFormData as any).consultation_price_currency || 'ARS';
  if (!compareValues(originalConsultationCurrency, newConsultationCurrency)) {
    partial.consultation_price_currency = newConsultationCurrency;
  }

  const originalPrice = Number((originalFormData as any).price || 0);
  const newPrice = Number((newFormData as any).price || 0);
  if (!compareValues(originalPrice, newPrice)) {
    partial.price = newPrice;
  }

  const originalPriceCurrency = (originalFormData as any).price_currency || 'ARS';
  const newPriceCurrency = (newFormData as any).price_currency || 'ARS';
  if (!compareValues(originalPriceCurrency, newPriceCurrency)) {
    partial.price_currency = newPriceCurrency;
  }

  const originalSubCategoryId = Number(originalAdditionalData.selectedSubCategory);
  const newSubCategoryId = Number(newAdditionalData.selectedSubCategory);
  if (!compareValues(originalSubCategoryId, newSubCategoryId) && newSubCategoryId > 0) {
    partial.preliminary_project_subcategory_id = newSubCategoryId;
  }

  const originalMaterialityId = Number(originalAdditionalData.selectedMaterialities[0] || 0);
  const newMaterialityId = Number(newAdditionalData.selectedMaterialities[0] || 0);
  if (!compareValues(originalMaterialityId, newMaterialityId)) {
    partial.materiality_id = newMaterialityId;
  }

  const originalUseId = Number(originalAdditionalData.selectedUses[0] || 0);
  const newUseId = Number(newAdditionalData.selectedUses[0] || 0);
  if (!compareValues(originalUseId, newUseId)) {
    partial.use_id = newUseId;
  }

  const originalPause = Boolean((originalFormData as any).pause || false);
  const newPause = Boolean((newFormData as any).pause || false);
  if (!compareValues(originalPause, newPause)) {
    partial.pause = newPause;
  }

  return partial;
};

const isServerFileId = (id: string): boolean => {
  return !isNaN(Number(id)) && !id.includes('-') && !id.includes('.');
};

export const getFileChanges = (
  initialFileItems: FileItem[],
  currentFileItems: FileItem[]
): { newFiles: FileItem[]; deletedFileIds: string[] } => {
  const newFiles = currentFileItems.filter(file => {
    return file.file && !isServerFileId(file.id);
  });

  const deletedFileIds = initialFileItems
    .filter(initial => {
      return !currentFileItems.find(curr => curr.id === initial.id);
    })
    .filter(file => isServerFileId(file.id))
    .map(file => file.id);

  return { newFiles, deletedFileIds };
};

